import {
  Panel,
  Skeleton,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';

type DiscoveredEnvironmentStats = {
  clusters?: number;
  virtualMachines?: number;
  volume?: number;
  loadBalancers?: number;
  commitments?: number;
};

type DiscoveredStatsGridProps = {
  stats: DiscoveredEnvironmentStats;
  isLoading: boolean;
  ready: boolean;
};

type MetricDefinition = {
  label: string;
  value: string | number | undefined;
};

const formatMetricValue = (value: string | number | undefined) => {
  if (value === undefined) {
    return '—';
  }

  return value;
};

const MetricCard = ({
  label,
  value,
  loading,
}: {
  label: string;
  value: string | number | undefined;
  loading: boolean;
}) => (
  <Panel sx={{ p: 16, flex: 1, minWidth: 0 }}>
    {loading ? (
      <Skeleton height={52} />
    ) : (
      <Stack gap={9}>
        <Typography
          variant="L10M"
          color={tokens.theme.fg.structural.secondary}
          sx={{ textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
        <Typography variant="h4" color={tokens.theme.fg.structural.primary}>
          {formatMetricValue(value)}
        </Typography>
      </Stack>
    )}
  </Panel>
);

export const DiscoveredStatsGrid = ({
  stats,
  isLoading,
  ready,
}: DiscoveredStatsGridProps) => {
  const showSkeleton = !ready || isLoading;

  const metrics: MetricDefinition[] = [
    { label: 'Clusters', value: stats.clusters },
    { label: 'Virtual machines', value: stats.virtualMachines },
    { label: 'Volume', value: stats.volume },
    { label: 'Load balancers', value: stats.loadBalancers },
    { label: 'Commitments', value: stats.commitments },
  ];

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: '100%',
        pl: 20,
        pr: 24,
        pt: 24,
      }}
    >
      <Panel
        sx={{
          p: 16,
          backgroundColor: tokens.theme.bg.structural.surfaceRaised,
          borderColor: tokens.theme.border.structural.neutral,
        }}
      >
        <Stack gap={16}>
          <Stack gap={2}>
            <Typography
              variant="L10M"
              color={tokens.theme.fg.structural.secondary}
              sx={{ textTransform: 'uppercase' }}
            >
              Discovered in your environment
            </Typography>
            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
            >
              {ready ? 'Read-only · nothing changed' : 'Scanning…'}
            </Typography>
          </Stack>

          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 10,
            }}
          >
            {metrics.map((metric) => (
              <MetricCard
                key={metric.label}
                label={metric.label}
                value={metric.value}
                loading={showSkeleton}
              />
            ))}
          </Stack>
        </Stack>
      </Panel>
    </Stack>
  );
};
